import { useEffect, useRef, useState, type ReactNode } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import type { FilterOption } from '@components/FilterDropdown';

import s from './DropdownChip.module.css';

type DropdownChipProps<T extends string> = {
  label: string;
  value: T | 'all';
  options: FilterOption<T>[];
  onChange: (v: T | 'all') => void;
  icon?: ReactNode;
};

function DropdownChip<T extends string>({
  label,
  value,
  options,
  onChange,
  icon,
}: DropdownChipProps<T>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const isActive = value !== 'all';
  const active = options.find((o) => o.value === value);

  return (
    <div ref={ref} className={s.wrap}>
      <button
        type="button"
        className={`${s.chip} ${isActive ? s.chipActive : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}>
        {icon}
        <span className={s.chipLabel}>{isActive && active ? active.label : label}</span>
        <KeyboardArrowDownIcon
          className={`${s.chevron} ${open ? s.chevronOpen : ''}`}
          sx={{ fontSize: 15 }}
        />
      </button>

      {open && (
        <div className={s.menu} role="listbox">
          {options.map((o) => {
            const selected = o.value === value;
            return (
              <div
                key={o.value}
                role="option"
                aria-selected={selected}
                className={`${s.menuItem} ${selected ? s.menuItemActive : ''}`}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}>
                <span className={s.menuItemLabel}>{o.label}</span>
                {selected && <CheckIcon className={s.menuCheck} sx={{ fontSize: 16 }} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default DropdownChip;
